import { Link } from 'react-router-dom';
import { ArrowRight, Shield, FileCheck, Users, AlertTriangle } from 'lucide-react';

const safetyItems = [
  {
    icon: Users,
    title: 'Toolbox Talks',
    description: 'Daily safety briefings ensure every team member understands site-specific hazards and protocols.',
  },
  {
    icon: AlertTriangle,
    title: 'Hazard Assessments',
    description: 'Comprehensive risk assessments before every project, with mitigation strategies documented.',
  },
  {
    icon: FileCheck,
    title: 'Documentation',
    description: 'Complete safety documentation and inspection records maintained for every project.',
  },
  {
    icon: Shield,
    title: 'Lockout/Tagout',
    description: 'Strict LOTO procedures for all electrical work, protecting workers and equipment.',
  },
];

const certifications = [
  { title: 'ESA Licensed', number: '# 7018736' },
  { title: 'WSIB Clearance', number: 'Active' },
  { title: 'COR Certified', number: 'In Process' },
  { title: 'Tarion Registered', number: 'Active' },
];

export default function SafetyPage() {
  return (
    <main className="pt-20" data-testid="safety-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <p className="section-label mb-4 text-[#F5F1EC]/60">Safety & Compliance</p>
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Safety isn't a slogan — <br/>
            <span className="italic text-[#B8A066]">it's a system.</span>
          </h1>
          <p 
            className="text-lg text-[#F5F1EC]/60 max-w-2xl"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Every project starts with a safety plan and ends with documentation. Our approach keeps teams safe and sites compliant.
          </p>
        </div>
      </section>

      {/* Safety Practices */}
      <section className="py-24 md:py-32 bg-[#F5F1EC]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="section-label mb-4">Our Practices</p>
            <h2 
              className="text-3xl md:text-4xl text-[#3D3A38]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
            >
              How we keep sites <span className="italic text-[#B8A066]">safe.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {safetyItems.map((item, index) => (
              <div 
                key={index}
                className="p-8 bg-[#3D3A38] group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#B8A066]/30 flex-shrink-0">
                    <item.icon size={24} className="text-[#B8A066]" />
                  </div>
                  <div>
                    <h3 
                      className="text-xl text-[#F5F1EC] mb-2"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {item.title}
                    </h3>
                    <p 
                      className="text-sm text-[#F5F1EC]/60 leading-relaxed"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="section-label mb-4 text-[#F5F1EC]/60">Compliance</p>
            <h2 
              className="text-3xl md:text-4xl text-[#F5F1EC]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
            >
              Certifications & <span className="italic text-[#B8A066]">Registrations</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {certifications.map((cert, index) => (
              <div key={index} className="text-center">
                <p 
                  className="text-lg text-[#F5F1EC] mb-1"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  {cert.title}
                </p>
                <p 
                  className="text-sm text-[#B8A066]"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {cert.number}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Compliance */}
      <section className="py-24 md:py-32 bg-[#F5F1EC]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="section-label mb-4">Code Compliance</p>
              <h2 
                className="text-3xl md:text-4xl text-[#3D3A38] mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
              >
                ESA standards, <span className="italic text-[#B8A066]">every time.</span>
              </h2>
              <div className="space-y-4 text-[#5C5855]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <p className="leading-relaxed">
                  All our electrical work is performed in accordance with the Ontario Electrical Safety Code 
                  and inspected by the Electrical Safety Authority (ESA).
                </p>
                <p className="leading-relaxed">
                  We maintain current knowledge of code updates and best practices through ongoing training 
                  and professional development. Our team includes licensed 309A electricians with 
                  extensive experience in commercial, industrial, and residential applications.
                </p>
              </div>
              <ul className="mt-8 space-y-3">
                {['Ontario Electrical Safety Code compliant', 'ESA inspection coordination', 'Permit management', 'As-built documentation'].map((item, i) => (
                  <li 
                    key={i}
                    className="text-xs tracking-[0.1em] uppercase text-[#5C5855] flex items-center gap-3"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    <span className="w-6 h-px bg-[#B8A066]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="h-[400px]">
              <img 
                src="https://images.unsplash.com/photo-1607631697491-61972eecf928?w=800&q=80"
                alt="Electrical panel"
                className="w-full h-full object-cover grayscale"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#3D3A38]">
        <div className="container-custom text-center">
          <h2 
            className="text-3xl md:text-4xl text-[#F5F1EC] mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 400 }}
          >
            Questions about compliance?
          </h2>
          <p 
            className="text-[#F5F1EC]/60 mb-8 max-w-xl mx-auto"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            We're happy to discuss our safety practices and certifications in detail.
          </p>
          <Link 
            to="/contact"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="safety-cta"
          >
            Contact Us <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
